const CONSENT_STORAGE_KEY = 'lc_analytics_consent_v1';

type Primitive = string | number | boolean;
type EventParams = Record<string, Primitive | null | undefined>;

const BLOCKED_PARAM_KEY = /(email|mail|name|phone|token|password|secret|address|fullName)/i;
const MAX_PARAM_LENGTH = 200;

let initialized = false;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    clarity?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
    lintrk?: (...args: unknown[]) => void;
    _linkedin_data_partner_ids?: string[];
  }
}

function parseBoolean(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined) return fallback;
  return value.toLowerCase() === 'true';
}

function readConsent(): boolean | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  if (raw === 'granted') return true;
  if (raw === 'denied') return false;
  return null;
}

function hasConsent(): boolean {
  const requireConsent = parseBoolean(import.meta.env.VITE_ANALYTICS_REQUIRE_CONSENT, true);
  if (!requireConsent) return true;
  return readConsent() === true;
}

function appendScript(id: string, src: string, text?: string): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  if (src) {
    script.src = src;
    script.async = true;
  }
  if (text) {
    script.text = text;
  }
  document.head.appendChild(script);
}

function sanitizeParams(params: EventParams): Record<string, Primitive> {
  const sanitized: Record<string, Primitive> = {};
  for (const [key, value] of Object.entries(params)) {
    if (BLOCKED_PARAM_KEY.test(key)) continue;
    if (value === null || value === undefined) continue;
    if (typeof value === 'string') {
      sanitized[key] = value.slice(0, MAX_PARAM_LENGTH);
      continue;
    }
    sanitized[key] = value;
  }
  return sanitized;
}

export function isAnalyticsConfigured(): boolean {
  return Boolean(
    import.meta.env.VITE_GTM_ID ||
      import.meta.env.VITE_GA4_MEASUREMENT_ID ||
      import.meta.env.VITE_CLARITY_PROJECT_ID ||
      import.meta.env.VITE_LINKEDIN_PARTNER_ID
  );
}

export function getAnalyticsConsent(): boolean | null {
  return readConsent();
}

export function setAnalyticsConsent(granted: boolean): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(CONSENT_STORAGE_KEY, granted ? 'granted' : 'denied');
  if (granted) {
    initAnalytics();
  }
}

export function initAnalytics(): void {
  if (initialized || typeof window === 'undefined' || !isAnalyticsConfigured() || !hasConsent()) return;

  const gtmId = import.meta.env.VITE_GTM_ID?.trim();
  const ga4Id = import.meta.env.VITE_GA4_MEASUREMENT_ID?.trim();
  const clarityId = import.meta.env.VITE_CLARITY_PROJECT_ID?.trim();
  const linkedinPartnerId = import.meta.env.VITE_LINKEDIN_PARTNER_ID?.trim();

  window.dataLayer = window.dataLayer ?? [];

  if (gtmId) {
    window.dataLayer.push({ event: 'gtm_init', ts: Date.now() });
    appendScript('lc-gtm-script', `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`);
  }

  if (ga4Id) {
    appendScript('lc-ga4-loader', `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4Id)}`);
    if (!window.gtag) {
      window.gtag = function gtag(...args: unknown[]) {
        window.dataLayer?.push(args as unknown as Record<string, unknown>);
      };
    }
    window.gtag('js', new Date());
    window.gtag('config', ga4Id, {
      anonymize_ip: true,
      allow_google_signals: false,
    });
  }

  if (clarityId) {
    appendScript(
      'lc-clarity-inline',
      '',
      `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${clarityId}");`
    );
  }

  if (linkedinPartnerId) {
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids ?? [];
    if (!window._linkedin_data_partner_ids.includes(linkedinPartnerId)) {
      window._linkedin_data_partner_ids.push(linkedinPartnerId);
    }
    appendScript(
      'lc-linkedin-inline',
      '',
      `window.lintrk=window.lintrk||function(a,b){window.lintrk.q.push([a,b])};window.lintrk.q=window.lintrk.q||[];`
    );
    appendScript('lc-linkedin-loader', 'https://snap.licdn.com/li.lms-analytics/insight.min.js');
  }

  initialized = true;
}

export function trackEvent(eventName: string, params: EventParams = {}): void {
  if (!hasConsent() || typeof window === 'undefined') return;
  initAnalytics();
  const safeParams = sanitizeParams(params);
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ event: eventName, ...safeParams });
  if (window.gtag) {
    window.gtag('event', eventName, safeParams);
  }
  if (window.lintrk) {
    window.lintrk('track', { conversion_id: eventName });
  }
}

export function trackPageView(path: string): void {
  trackEvent('page_view', { page_path: path });
}
